import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { verifyTokenAPI } from '../api/auth.api';

export type Region = 'CA' | 'US';

export interface User {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  name?: string;
  type?: 'user' | 'admin' | 'superadmin';
  permissions?: string[];
  assignedStores?: number[];
  /** Regions the user is allowed to access. Present in the JWT payload from the backend. */
  regions: Region[];
  /** Default region — used until the user picks another via the RegionSwitcher. */
  defaultRegion: Region;
  /** Present on `/users` list responses; absent from JWT-derived user (login /me). */
  createdAt?: string;
  updatedAt?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // React Strict Mode double-invokes effects in dev. This ref guards against
  // the second invocation firing another /auth/me — one-time init is one-time.
  const verifyOnceRef = useRef(false);

  useEffect(() => {
    if (verifyOnceRef.current) return;
    verifyOnceRef.current = true;

    const savedToken = localStorage.getItem('authToken');
    if (!savedToken) {
      setIsLoading(false);
      return;
    }
    setToken(savedToken);
    verifyTokenAPI()
      .then((res) => {
        if (res?.user) setUser(res.user as User);
        else {
          localStorage.removeItem('authToken');
          setToken(null);
        }
      })
      .catch(() => {
        localStorage.removeItem('authToken');
        setToken(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback((newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('authToken', newToken);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentRegion');
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;
      if (user.type === 'admin' || user.type === 'superadmin') return true;
      return (user.permissions ?? []).includes(permission);
    },
    [user],
  );

  const isAdmin = user?.type === 'admin' || user?.type === 'superadmin';
  const isSuperAdmin = user?.type === 'superadmin';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        logout,
        isAuthenticated: !!token,
        hasPermission,
        isAdmin,
        isSuperAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
